import { useTranslation } from "react-i18next"

import type { PrismViewPreference } from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  FILTH_SEARCH_VISUALIZATION_FEATURE_ID,
  PRISM_VIEW_COMPANY,
} from "@/features/chat/components/prism-retrieval/prism-retrieval-constants"
import { isPrismView } from "@/features/chat/components/prism-retrieval/prism-retrieval-utils"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { useGeneralSettings } from "@/hooks/use-module-settings"

import { PRISM_VIEW_PREFERENCE_OPTIONS } from "./prism-view-constants"

// Mirrors resolvePrismView: a known view shows as itself; unset, "company"
// and an unknown stored value all defer to the company default, so they show
// as "company" — the choice the user actually gets.
function displayedPreference(stored: unknown): PrismViewPreference {
  return isPrismView(stored) ? stored : PRISM_VIEW_COMPANY
}

/**
 * Per-user Prism retrieval view (#3261). Shown only while secure neural
 * retrieval is enabled for this user; "company" defers to the admin default.
 */
export function PrismViewSelector() {
  const { t } = useTranslation()
  const visualizationEnabled = useFeatureEnabled(FILTH_SEARCH_VISUALIZATION_FEATURE_ID)
  const { settings, updateSettings } = useGeneralSettings()

  if (!visualizationEnabled) return null

  const value = displayedPreference(settings?.prism_view)

  function handleChange(next: string) {
    void updateSettings({ prism_view: next as PrismViewPreference })
  }

  return (
    <div className="space-y-2" data-testid="prism-view-setting">
      <Label className="text-sm font-medium" htmlFor="prism-view">
        {t("personalization.preferences.prismView.label", { defaultValue: "Prism view" })}
      </Label>
      <Select value={value} onValueChange={handleChange}>
        <SelectTrigger id="prism-view">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRISM_VIEW_PREFERENCE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {t(option.labelKey, { defaultValue: option.fallback })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">
        {t("personalization.preferences.prismView.hint", {
          defaultValue: "How secure retrieval is shown while an answer is assembled.",
        })}
      </p>
    </div>
  )
}
